import React, { useState, useRef } from 'react';
import { Lock, Plus, X, Check, Library, ChevronDown } from 'lucide-react';
import { boardsAPI } from '../../../api';
import LibraryModal from '../../../components/LibraryModal';

/**
 * Private Space — owner-only area at the bottom of the Board Editor.
 * Notes, to-dos and a private gallery. Never rendered on the client
 * view; the public API strips these fields entirely.
 */

const eyebrowStyle = {
  fontFamily: 'Playfair Display, serif',
  fontSize: '11px',
  letterSpacing: '0.24em',
  textTransform: 'uppercase',
  color: 'var(--kahla-text-light)',
};

const cardStyle = {
  background: 'var(--kahla-white)',
  border: '1px solid var(--kahla-border)',
  padding: '20px',
};

const resolveUrl = (url) => (url || '').startsWith('r2/')
  ? `${process.env.REACT_APP_BACKEND_URL}/api/images/${url}`
  : url;

const PrivateNotes = ({ boardId, notes, t, onChanged }) => {
  const [adding, setAdding] = useState(false);
  const addNote = async () => {
    if (adding) return;
    setAdding(true);
    try { await boardsAPI.privateSpace.addNote(boardId, { title: '', content: '' }); await onChanged(); }
    finally { setAdding(false); }
  };
  return (
    <div style={cardStyle} data-testid="private-notes-block">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: notes.length ? '16px' : '10px' }}>
        <span style={eyebrowStyle}>{t.private_notes_title || 'Private Notes'}</span>
        <button
          type="button"
          onClick={addNote}
          data-testid="private-add-note-btn"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            background: 'transparent', border: '1px solid var(--kahla-border)',
            padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit',
            fontSize: '12px', color: 'var(--kahla-text)',
          }}
        ><Plus size={13} /> {t.private_add_note || 'Add note'}</button>
      </div>
      {notes.length === 0 && (
        <p style={{ fontSize: '12px', fontStyle: 'italic', color: 'var(--kahla-text-light)', margin: 0 }}>
          {t.private_empty_notes || 'No notes yet — ideas, shot lists, reminders.'}
        </p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {notes.map((note) => (
          <div key={note.id} style={{ background: 'var(--kahla-cream-light)', border: '1px solid var(--kahla-border)', padding: '14px 16px', position: 'relative' }} data-testid={`private-note-${note.id}`}>
            <button
              type="button"
              onClick={async () => { await boardsAPI.privateSpace.deleteNote(boardId, note.id); await onChanged(); }}
              data-testid={`private-note-delete-${note.id}`}
              title={t.private_delete || 'Delete'}
              style={{ position: 'absolute', top: '8px', right: '8px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--kahla-text-light)', padding: '4px' }}
            ><X size={14} /></button>
            <input
              type="text"
              defaultValue={note.title || ''}
              maxLength={120}
              placeholder={t.private_note_title_placeholder || 'Note title'}
              onBlur={async (e) => {
                const v = e.target.value.slice(0, 120);
                if (v !== (note.title || '')) { await boardsAPI.privateSpace.updateNote(boardId, note.id, { title: v }); await onChanged(); }
              }}
              data-testid={`private-note-title-${note.id}`}
              style={{
                width: '100%', border: 'none', outline: 'none', background: 'transparent',
                fontFamily: 'Playfair Display, serif', fontSize: '15px',
                color: 'var(--kahla-charcoal)', marginBottom: '6px', paddingRight: '24px',
              }}
            />
            <textarea
              defaultValue={note.content || ''}
              maxLength={4000}
              rows={3}
              placeholder={t.private_note_placeholder || 'Write anything…'}
              onBlur={async (e) => {
                const v = e.target.value.slice(0, 4000);
                if (v !== (note.content || '')) { await boardsAPI.privateSpace.updateNote(boardId, note.id, { content: v }); await onChanged(); }
              }}
              data-testid={`private-note-content-${note.id}`}
              style={{
                width: '100%', border: 'none', outline: 'none', background: 'transparent',
                fontFamily: 'inherit', fontSize: '13px', lineHeight: 1.6,
                color: 'var(--kahla-text)', resize: 'vertical',
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const PrivateTodos = ({ boardId, todos, t, onChanged }) => {
  const [text, setText] = useState('');
  const add = async () => {
    const v = text.trim();
    if (!v) return;
    setText('');
    await boardsAPI.privateSpace.addTodo(boardId, v);
    await onChanged();
  };
  return (
    <div style={cardStyle} data-testid="private-todos-block">
      <span style={{ ...eyebrowStyle, display: 'block', marginBottom: '14px' }}>
        {t.private_todo_title || 'To-Do / Reminders'}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {todos.map((todo) => (
          <div key={todo.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }} data-testid={`private-todo-${todo.id}`}>
            <button
              type="button"
              onClick={async () => { await boardsAPI.privateSpace.updateTodo(boardId, todo.id, { done: !todo.done }); await onChanged(); }}
              data-testid={`private-todo-toggle-${todo.id}`}
              style={{
                width: '18px', height: '18px', flexShrink: 0, cursor: 'pointer',
                border: '1px solid var(--kahla-taupe)', background: todo.done ? 'var(--kahla-taupe)' : 'transparent',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}
            >{todo.done && <Check size={12} color="#fff" />}</button>
            <span style={{
              flex: 1, fontSize: '13px', color: todo.done ? 'var(--kahla-text-light)' : 'var(--kahla-text)',
              textDecoration: todo.done ? 'line-through' : 'none',
            }}>{todo.text}</span>
            <button
              type="button"
              onClick={async () => { await boardsAPI.privateSpace.deleteTodo(boardId, todo.id); await onChanged(); }}
              data-testid={`private-todo-delete-${todo.id}`}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--kahla-text-light)', padding: '2px' }}
            ><X size={13} /></button>
          </div>
        ))}
      </div>
      <input
        type="text"
        value={text}
        maxLength={300}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
        placeholder={t.private_todo_placeholder || 'Add a task and press Enter'}
        data-testid="private-todo-input"
        style={{
          width: '100%', marginTop: todos.length ? '14px' : 0,
          padding: '8px 10px', fontSize: '13px', fontFamily: 'inherit',
          background: 'var(--kahla-cream-light)', border: '1px solid var(--kahla-border)',
          color: 'var(--kahla-text)', outline: 'none', boxSizing: 'border-box',
        }}
      />
    </div>
  );
};

const PrivateGallery = ({ boardId, images, t, onChanged, showToast, lang }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        try { await boardsAPI.privateSpace.uploadImage(boardId, f); }
        catch (_err) { showToast?.(t.save_failed || 'Could not save'); }
      }
      await onChanged();
    } finally { setUploading(false); }
  };
  return (
    <div style={cardStyle} data-testid="private-gallery-block">
      <span style={{ ...eyebrowStyle, display: 'block', marginBottom: '4px' }}>
        {t.private_gallery_title || 'Private Gallery'}
      </span>
      <p style={{ fontSize: '12px', fontStyle: 'italic', color: 'var(--kahla-text-light)', margin: '0 0 14px' }}>
        {t.private_gallery_hint || 'Images & inspiration only you can see.'}
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '10px' }}>
        {images.map((img) => (
          <div key={img.id} style={{ position: 'relative', aspectRatio: '3/4', overflow: 'hidden', background: 'var(--kahla-cream-light)' }} data-testid={`private-image-${img.id}`}>
            <img src={resolveUrl(img.url)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            <button
              type="button"
              onClick={async () => { await boardsAPI.privateSpace.deleteImage(boardId, img.id); await onChanged(); }}
              data-testid={`private-image-delete-${img.id}`}
              style={{
                position: 'absolute', top: '6px', right: '6px',
                width: '22px', height: '22px', borderRadius: '50%',
                background: 'rgba(42,38,32,0.65)', border: 'none', cursor: 'pointer',
                color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
              }}
            ><X size={12} /></button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          data-testid="private-gallery-add-btn"
          style={{
            aspectRatio: '3/4', border: '1px dashed var(--kahla-taupe)',
            background: 'transparent', cursor: uploading ? 'wait' : 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: '6px', color: 'var(--kahla-text-light)', fontFamily: 'inherit', fontSize: '12px',
          }}
        >
          <Plus size={18} />
          {uploading ? (t.uploading || 'Uploading…') : (t.private_add_images || 'Add images')}
        </button>
        <button
          type="button"
          onClick={() => setShowLibrary(true)}
          data-testid="private-gallery-library-btn"
          style={{
            aspectRatio: '3/4', border: '1px dashed var(--kahla-border)',
            background: 'transparent', cursor: 'pointer',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: '6px', color: 'var(--kahla-text-light)', fontFamily: 'inherit', fontSize: '12px',
          }}
        >
          <Library size={18} />
          {t.private_add_from_library || 'From library'}
        </button>
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" multiple style={{ display: 'none' }} onChange={onFiles} data-testid="private-gallery-file-input" />
      {showLibrary && (
        <LibraryModal
          onClose={() => setShowLibrary(false)}
          onSelect={async (libraryImageIds) => {
            setShowLibrary(false);
            try {
              await boardsAPI.privateSpace.addFromLibrary(boardId, libraryImageIds);
              await onChanged();
            } catch (_err) {
              showToast?.(t.save_failed || 'Could not save');
            }
          }}
          sectionLabels={[]}
          lang={lang}
        />
      )}
    </div>
  );
};

const PrivateSpace = ({ board, boardId, t, onChanged, showToast, lang = 'en' }) => {
  const storageKey = `maestra_private_space_open_${boardId}`;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(storageKey) === '1'; } catch (e) { return false; }
  });
  const toggle = () => {
    setOpen((v) => {
      try { localStorage.setItem(storageKey, v ? '0' : '1'); } catch (e) {}
      return !v;
    });
  };
  return (
    <section data-testid="private-space-section" style={{ marginTop: '72px', paddingBottom: '24px' }}>
      {/* Divider with lock — the whole header row toggles collapse */}
      <button
        type="button"
        onClick={toggle}
        data-testid="private-space-toggle"
        aria-expanded={open}
        style={{
          display: 'flex', alignItems: 'center', gap: '18px', width: '100%',
          background: 'transparent', border: 'none', cursor: 'pointer',
          padding: 0, marginBottom: '10px',
        }}
      >
        <div style={{ flex: 1, height: '1px', background: 'var(--kahla-border)' }} />
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', ...eyebrowStyle, color: 'var(--kahla-taupe)', fontSize: '12px' }}>
          <Lock size={13} />
          {t.private_space_title || 'Private Space'}
          <ChevronDown
            size={14}
            style={{
              transition: 'transform 250ms ease',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
        </span>
        <div style={{ flex: 1, height: '1px', background: 'var(--kahla-border)' }} />
      </button>
      <p
        data-testid="private-space-hint"
        style={{
          textAlign: 'center', margin: open ? '0 0 28px' : 0,
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic', fontSize: '13px', color: 'var(--kahla-text-light)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
        }}
      >
        <Lock size={11} /> {t.private_space_hint || 'Visible only to you'}
      </p>
      {open && (
        <div data-testid="private-space-content" style={{ display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: '860px', margin: '0 auto' }}>
          <PrivateNotes boardId={boardId} notes={board.private_notes || []} t={t} onChanged={onChanged} />
          <PrivateTodos boardId={boardId} todos={board.private_todos || []} t={t} onChanged={onChanged} />
          <PrivateGallery boardId={boardId} images={board.private_gallery || []} t={t} onChanged={onChanged} showToast={showToast} lang={lang} />
        </div>
      )}
    </section>
  );
};

export default PrivateSpace;
